import React, { useCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Flex } from "../../../../components/Flex";
import { Spacing } from "../../../../theme/Spacing";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { CreateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { useStepperRegisterValidation } from "../../../../components/stepper/useStepperRegisterValidation";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { FontSize, Text } from "../../../../components/Text";
import { Input } from "../../../../components/Input";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { faTextWidth } from "@fortawesome/free-solid-svg-icons/faTextWidth";
import { Textarea } from "../../../../components/Textarea";
import { faIcons } from "@fortawesome/free-solid-svg-icons/faIcons";
import { Button } from "../../../../components/Button";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import { IconSelectModal } from "../../../../components/IconSelectModal";
import { getFontAwesomeIcon } from "../../../../utils/getFontAwesomeIcon";
import { TestIDs } from "../../../../tests/types/testIds";

const Container = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xl};
  width: 50%;
  flex-wrap: wrap;
  align-self: flex-start;
`;

const CurrentIconContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  border-inline-start: ${({ theme }) => `4px solid ${theme.main}`};
  padding-inline-start: ${Spacing.s};
`;

const OptionalSpan = styled.span`
  font-size: ${FontSize.m};
  font-style: italic;
`;

const IconsButton = styled(Button)`
  width: fit-content;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  border: ${({ theme }) => `1px solid ${theme.main}`};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.sub};
  }
`;

export const ModuleDefinitionCreateEditBasicInfoStep: React.FC = () => {
  const [iconsModalVisible, setIconsModalVisible] = useState(false);

  const { t } = useTranslation();
  const { currentStep } = useStepContext();

  const { control, watch, setValue } = useFormContext<
    CreateModuleDefinitionDto | UpdateModuleDefinitionDto
  >();

  const [name, icon] = watch(["name", "icon"]);

  const canGoNext = useCallback(() => {
    return Boolean(
      name &&
        name?.length <= Lengths.DESCRIPTION &&
        name.length >= Lengths.MIN_3,
    );
  }, [name]);

  useStepperRegisterValidation(currentStep, canGoNext);

  return (
    <Container>
      <Controller
        name="name"
        control={control}
        rules={{
          required: true,
          minLength: {
            value: Lengths.MIN_3,
            message: t("error.length.greater", {
              name: t("modules.definition.basicInfo.name.title"),
              length: Lengths.MIN_3,
            }),
          },
          maxLength: {
            value: Lengths.DESCRIPTION,
            message: t("error.length.less", {
              name: t("modules.definition.basicInfo.name.title"),
              length: Lengths.DESCRIPTION,
            }),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Flex flexDirection="column" gap="m" width={"100%"}>
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faCube} color="main" size="xl" />

              <Text fontSize="l" fontWeight={"bold"} color="main">
                {t("modules.definition.basicInfo.name.title")}
              </Text>
            </Flex>

            <Text color="darkerSub">{}</Text>

            <Input
              onChange={onChange}
              value={value}
              valid={!error?.message}
              errorMessage={error?.message}
              required
              maxLength={Lengths.DESCRIPTION}
              minLength={Lengths.MIN_3}
              title={t("modules.definition.basicInfo.name.description")}
              placeholder={t("modules.definition.basicInfo.name.placeholder")}
              dataTestId={TestIDs.modules.createEdit.stepBasicInfo.name.input}
            />
          </Flex>
        )}
      />

      <Controller
        name="description"
        control={control}
        rules={{
          required: true,
          maxLength: {
            value: Lengths.DESCRIPTION,
            message: t("error.length.less", {
              name: t("common.description"),
              length: Lengths.DESCRIPTION,
            }),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Flex flexDirection="column" gap="m" width={"100%"}>
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faTextWidth} color="main" size="xl" />

              <Text fontSize="l" fontWeight={"bold"} color="main">
                {t("common.description")}
                <OptionalSpan> ({t("common.optional")})</OptionalSpan>
              </Text>
            </Flex>

            <Textarea
              onChange={onChange}
              value={value}
              valid={!error?.message}
              errorMessage={error?.message}
              required
              maxLength={Lengths.DESCRIPTION}
              title={t("modules.definition.basicInfo.description.description")}
              placeholder={t(
                "modules.definition.basicInfo.description.placeholder",
              )}
              dataTestId={
                TestIDs.modules.createEdit.stepBasicInfo.description.input
              }
            />
          </Flex>
        )}
      />

      <Flex flexDirection="column" gap="m" width={"100%"}>
        <Flex alignItems="center" gap="s">
          <AtiraIcon icon={faIcons} color="main" size="xl" />

          <Text fontSize="l" fontWeight={"bold"} color="main">
            {t("modules.definition.basicInfo.icon.title")}

            <OptionalSpan> ({t("common.optional")})</OptionalSpan>
          </Text>
        </Flex>

        <Text color="darkerSub">
          {t("modules.definition.basicInfo.icon.description")}
        </Text>

        {icon ? (
          <CurrentIconContainer>
            <Text fontWeight={"bold"} color="main">
              {t("common.currentIcon")}:
            </Text>

            <AtiraIcon icon={getFontAwesomeIcon(icon) || faCube} size="lg" />
          </CurrentIconContainer>
        ) : null}

        <IconsButton
          onClick={() => setIconsModalVisible(true)}
          margin="none"
          icon={faAngleDown}
          dataTestId={TestIDs.modules.createEdit.stepBasicInfo.icon.button}
        >
          {t("modules.definition.basicInfo.icon.button")}
        </IconsButton>
      </Flex>

      <IconSelectModal
        open={iconsModalVisible}
        onClose={() => setIconsModalVisible(false)}
        onChange={(value) => setValue("icon", value)}
        currentIcon={icon}
      />
    </Container>
  );
};
